import React, { useState } from 'react';

import { useIntake } from '../context/IntakeContext';
import { useStepNav } from '../hooks/useStepNav';
import StepLayout from '../components/StepLayout';
import Field from '../components/Field';
import { Input } from '../components/ui/input';

/**
 * Step 1 — and the point at which the quote request starts existing.
 *
 * Everything after this is an update. That is the whole design: from the
 * moment the customer has told us who they are, the shop has a lead it can
 * act on, whether or not the customer ever reaches the end of the form.
 */
const ContactStep = () => {
  const { form, update, startDraft, started } = useIntake();
  const go = useStepNav();

  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!form.firstName?.trim()) next.firstName = 'Please enter your first name';
    if (!form.lastName?.trim()) next.lastName = 'Please enter your last name';

    const hasPhone = Boolean(form.phone?.trim());
    const hasEmail = Boolean(form.email?.trim());
    if (!hasPhone || !hasEmail) {
      next.phone = 'Please provide your phone number and email address';
    }
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = 'That email address does not look right';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (!validate()) return;

    // Fire and forget. Creating the request takes a network round trip the
    // customer has no reason to watch, and this is the step where waiting costs
    // the most — so they move on immediately and the record lands behind them.
    // A failure shows in the header's save indicator and is retried when they
    // continue from the next step.
    startDraft(form).catch(() => null);
    go('vehicle');
  };

  return (
    <StepLayout
      title="Your details"
      onNext={handleNext}
      nextLabel={started ? 'Continue' : 'Start my request'}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First name" required error={errors.firstName}>
          {({ id }) => (
            <Input
              id={id}
              name="firstName"
              autoComplete="given-name"
              value={form.firstName}
              onChange={(event) => update({ firstName: event.target.value })}
            />
          )}
        </Field>

        <Field label="Last name" required error={errors.lastName}>
          {({ id }) => (
            <Input
              id={id}
              name="lastName"
              autoComplete="family-name"
              value={form.lastName}
              onChange={(event) => update({ lastName: event.target.value })}
            />
          )}
        </Field>
      </div>

      <Field label="Mobile number" error={errors.phone}>
        {({ id }) => (
          <Input
            id={id}
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="021 123 4567"
            value={form.phone}
            onChange={(event) => update({ phone: event.target.value })}
          />
        )}
      </Field>

      <Field label="Email address" error={errors.email}>
        {({ id }) => (
          <Input
            id={id}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={(event) => update({ email: event.target.value })}
          />
        )}
      </Field>
    </StepLayout>
  );
};

export default ContactStep;
